import { etiquetasCortas, reunionesOf, type CubosReuniones, type EstadoReunion, type Snapshot } from '../lib/snapshot-types.js';
import { colorDe, iniciales } from '../lib/avatar.js';
import type { SessionState } from '../lib/session.js';
import { CalendarioConexion } from './CalendarioConexion.js';
import { MeetConexion } from './MeetConexion.js';
import { ChipEstado, horaReunion } from './MeetingsList.js';

/**
 * LAS REUNIONES DEL PERIODO (revamp del 6-oct): salen de las citas de GHL (y de
 * Zoho en quien lo usa) y se clasifican en cuatro estados, en el orden en que los
 * dijo Joan: por hacer, hechas, no show y propuesta.
 *
 *   - Una barra apilada de cuatro colores con su leyenda (cada estado abre su lista).
 *   - Las barras por persona, con su nombre.
 *   - La lista agrupada por estado: la cuenta de cada grupo es la de su estado, y
 *     la suma es el total de la tarjeta. Se ven unas pocas por grupo; el resto,
 *     en su lista.
 *
 * Sin la etapa de propuesta configurada, "Propuesta" es un guion (no se sabe), no 0.
 *
 * Las grabadas con Fluxo para Meet (7-oct) dicen «Grabada»; su resumen se lee en la lista. En
 * Fluxo, «Grabadas» abre todas las grabaciones, de cualquier fecha, con su transcripcion (27-oct). Una
 * grabacion sin cita de GHL entra como reunion hecha de quien graba. Desde el 12-oct (0054), una
 * grabacion en la que solo hablo quien grababa es un no show, y una prueba no cuenta.
 *
 * El total y la asistencia estan en la tarjeta de arriba (fila resumen) y el periodo en su
 * boton: aqui no se repiten (revision del 7-oct). «Por hacer» son las agendadas desde ahora
 * (el servidor: filtro.ts, cuentaEnPeriodo), asi que no sale a 0 por elegir un periodo que acaba hoy.
 */

export const ORDEN_ESTADOS: EstadoReunion[] = ['por_hacer', 'hecha', 'no_show', 'propuesta'];
const POR_GRUPO = 3;

/** El recuento de un estado en unos cubos (null: propuesta sin configurar). */
export function cuentaDe(c: CubosReuniones, e: EstadoReunion): number | null {
  return e === 'hecha' ? c.hechas : e === 'propuesta' ? c.propuesta : e === 'por_hacer' ? c.por_hacer : c.no_show;
}

const ROTULO_LEYENDA: Record<EstadoReunion, string> = { por_hacer: 'Por hacer', hecha: 'Hechas', no_show: 'No show', propuesta: 'Propuesta' };
const TITULO_LEYENDA: Record<EstadoReunion, string> = {
  por_hacer: 'Agendadas desde ahora (los próximos 30 días como poco)',
  hecha: 'Ya pasaron y nada dice que no se hicieran',
  no_show: 'Marcadas como no show, canceladas después de su hora o grabadas sin que nadie más hablara',
  propuesta: 'Hechas y con propuesta, presupuesto o contrato después',
};

export function ReunionesSeccion({ snapshot, session }: { snapshot: Snapshot; session?: SessionState }) {
  const r = reunionesOf(snapshot);
  const zona = snapshot.filtro?.zona ?? 'Europe/Madrid';
  const conexion = session?.client.slug === 'fluxo' ? <><CalendarioConexion session={session} /><MeetConexion session={session} /></> : null;
  const cabecera = (
    <div className="seccion-cabeza">
      <h2 className="section-title">Reuniones</h2>
      <span style={{ display: 'flex', gap: 12 }}>
        {session?.client.slug === 'fluxo' && <a className="enlace-suave" href="#/reuniones-grabadas">Grabadas</a>}
        {r.status === 'ok' && r.total > 0 && <a className="enlace-suave" href="#/meetings-list/all">Ver todas</a>}
      </span>
    </div>
  );
  if (r.status !== 'ok') {
    return (
      <section className="section reu">
        {cabecera}
        {conexion}
        <p className="section-empty">No se han podido leer las citas de GHL. Las cifras de reuniones salen con guion hasta que contesten.</p>
      </section>
    );
  }
  const total = r.total;
  const conValor = ORDEN_ESTADOS.map((e) => ({ estado: e, n: cuentaDe(r.cubos, e) }));
  const sumaBarra = conValor.reduce((a, x) => a + (x.n ?? 0), 0);
  const porPersona = r.por_persona.filter((p) => ORDEN_ESTADOS.some((e) => (cuentaDe(p, e) ?? 0) > 0));
  const maxPersona = Math.max(1, ...porPersona.map((p) => ORDEN_ESTADOS.reduce((a, e) => a + (cuentaDe(p, e) ?? 0), 0)));
  const corta = etiquetasCortas(porPersona.map((p) => p.persona));
  const grupos = ORDEN_ESTADOS
    .map((e) => ({ estado: e, items: r.items.filter((i) => i.estado === e) }))
    .filter((g) => g.items.length > 0);

  return (
    <section className="section reu" aria-label="Reuniones">
      {cabecera}
        {conexion}

      <div className="reu-barra" role="img" aria-label={conValor.map((x) => `${ROTULO_LEYENDA[x.estado]}: ${x.n ?? 'sin dato'}`).join(', ')}>
        {sumaBarra === 0
          ? <span className="reu-barra-vacia" />
          : conValor.filter((x) => (x.n ?? 0) > 0).map((x) => (
            <a key={x.estado} href={`#/meetings-list/${x.estado}`} className={`reu-seg reu-seg--${x.estado}`} style={{ width: `${(100 * (x.n ?? 0)) / sumaBarra}%` }} title={`${ROTULO_LEYENDA[x.estado]}: ${x.n}`}>
              <span className="sr">{ROTULO_LEYENDA[x.estado]}: {x.n}</span>
            </a>
          ))}
      </div>

      <ul className="reu-leyenda">
        {conValor.map((x) => (
          <li key={x.estado}>
            <a href={`#/meetings-list/${x.estado}`} className={`reu-ley reu-ley--${x.estado}`} title={x.n === null ? 'Sin la etapa de propuesta configurada' : TITULO_LEYENDA[x.estado]}>
              <i aria-hidden="true" />
              <span>{ROTULO_LEYENDA[x.estado]}</span>
              <strong>{x.n === null ? '-' : x.n}</strong>
            </a>
          </li>
        ))}
      </ul>

      {porPersona.length > 0 && (
        <div className="reu-personas">
          <div className="reu-sub">Por persona</div>
          {porPersona.map((p) => {
            const suya = ORDEN_ESTADOS.reduce((a, e) => a + (cuentaDe(p, e) ?? 0), 0);
            return (
              <div key={p.persona} className="reu-persona">
                <span className={p.persona === 'Sin asignar' ? 'av av--nadie' : `av av--${colorDe(p.persona)}`} aria-hidden="true">{p.persona === 'Sin asignar' ? '?' : iniciales(p.persona)}</span>
                <span className="reu-persona-nombre" title={p.persona}>{corta.get(p.persona) ?? p.persona}</span>
                <span className="reu-persona-pista">
                  <span className="reu-persona-barra" style={{ width: `${(100 * suya) / maxPersona}%` }}>
                    {ORDEN_ESTADOS.map((e) => {
                      const n = cuentaDe(p, e) ?? 0;
                      return n > 0 ? <i key={e} className={`reu-seg--${e}`} style={{ flexGrow: n }} title={`${ROTULO_LEYENDA[e]}: ${n}`} /> : null;
                    })}
                  </span>
                </span>
                <strong className="reu-persona-total">{suya}</strong>
              </div>
            );
          })}
        </div>
      )}

      {total === 0 ? (
        <p className="section-empty">Sin reuniones en este periodo. Salen de las citas de los calendarios de GHL y de las grabaciones de Fluxo para Meet.</p>
      ) : (
        <div className="reu-grupos">
          {grupos.map((g) => (
            <div key={g.estado} className="reu-grupo">
              <div className="reu-grupo-cabeza">
                <ChipEstado estado={g.estado} />
                <span className="reu-grupo-n">{g.items.length}</span>
              </div>
              <ul>
                {g.items.slice(0, POR_GRUPO).map((it) => (
                  <li key={it.id}>
                    <a className="reu-item" href={`#/meetings-list/${g.estado}`}>
                      <span className="reu-item-titulo">{it.titulo}</span>
                      <span className="reu-item-meta">{it.persona} · {horaReunion(it.inicio, zona)}{it.grabada ? ' · Grabada' : ''}</span>
                    </a>
                  </li>
                ))}
              </ul>
              {g.items.length > POR_GRUPO && (
                <a className="enlace-suave reu-mas" href={`#/meetings-list/${g.estado}`}>
                  Ver las {g.items.length}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
